import React, { useState } from "react";
import { Info, Plus, Check, Loader2, Lock, ShieldCheck, ArrowRight, Landmark, Wallet, Smartphone } from "lucide-react";
import { Input } from "./ui/input";
import { inr } from "../lib/format";
import { GOLD, GLASS_INPUT, GlassCard, CardHeader, StepRail, Cta, AmountChips, AmountField } from "./WalletUI";

const CHIPS = [500, 1000, 2000, 5000, 10000, 25000];
const MIN_WD = 100;
const mask = (n) => `••••${String(n || "").slice(-4)}`;

function Panel({ children, testid }) {
  return <div className="relative px-4 pb-5 pt-4 space-y-4" data-testid={testid}>{children}</div>;
}

function SavedAccount({ a, active, onPick }) {
  return (
    <button type="button" onClick={onPick} data-testid={`wd-saved-${a.accountNumber}`}
      className={`w-full text-left rounded-2xl p-[1px] transition-[transform,box-shadow] active:scale-[0.99] ${
        active ? "bg-[linear-gradient(135deg,#ffe08a,#f5b400_40%,rgba(245,180,0,0.15))] shadow-[0_12px_30px_-14px_rgba(245,180,0,0.7)]" : "bg-white/10 hover:bg-white/20"}`}>
      <span className="flex items-center gap-3 rounded-2xl bg-[#0d1117]/95 px-3.5 py-3">
        <span className={`grid place-items-center h-10 w-10 rounded-xl shrink-0 ${active ? `${GOLD} text-black` : "bg-white/5 text-slate-300 ring-1 ring-white/10"}`}><Landmark className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-bold text-white truncate">{a.accountName}</span>
          <span className="block text-[11px] text-slate-400 tabular-nums tracking-wider">{mask(a.accountNumber)} · {a.ifsc}</span>
        </span>
        <span className={`grid place-items-center h-6 w-6 rounded-full ${active ? `${GOLD} text-black` : "ring-1 ring-white/15 text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
      </span>
    </button>
  );
}

export default function WithdrawFlow({ balance, wagerLeft, savedAccounts, savedMax, wd, setWd, onSubmit, submitting }) {
  const [step, setStep] = useState(1);
  const [addNew, setAddNew] = useState(savedAccounts.length === 0);
  const n = Number(wd.amount) || 0;
  const locked = wagerLeft > 0;
  const amountOk = n >= MIN_WD && n <= balance;
  const isUpi = wd.method === "upi";
  const upiOk = /^[a-z0-9][a-z0-9._-]{1,48}@[a-z][a-z0-9]{1,30}$/i.test((wd.upiId || "").trim());
  const bankOk = isUpi ? upiOk : (wd.accountName.trim().length >= 2 && /^\d{9,18}$/.test(wd.accountNumber) && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(wd.ifsc));
  const setMethod = (m) => setWd({ ...wd, method: m });
  const selectedSaved = savedAccounts.find((a) => a.accountNumber === wd.accountNumber && a.ifsc === wd.ifsc);
  const pickSaved = (a) => { setWd({ ...wd, accountName: a.accountName, accountNumber: a.accountNumber, ifsc: a.ifsc }); setAddNew(false); };

  const steps = [
    { key: 1, label: "Amount", testid: "wd-step-1" },
    { key: 2, label: isUpi ? "UPI ID" : "Bank", testid: "wd-step-2", enabled: amountOk },
    { key: 3, label: "Review", testid: "wd-step-3", enabled: amountOk && bankOk },
  ];

  return (
    <GlassCard testid="withdraw-flow">
      <CardHeader title="Withdraw" sub="Zero fees · processed within 2 hours" icon={ShieldCheck} />

      <div className="relative mx-4 mt-4 flex items-center justify-between rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-3.5 py-3" data-testid="wd-withdrawable">
        <span className="inline-flex items-center gap-2 text-[12px] text-slate-300"><Wallet className="h-4 w-4 text-[#f5b400]" /> Withdrawable</span>
        <span className="font-display font-extrabold text-lg text-white tabular-nums">{inr(balance)}</span>
      </div>
      {locked && (
        <div className="relative mx-4 mt-2 flex items-center gap-2 rounded-2xl ring-1 ring-amber-400/40 bg-amber-500/10 px-3.5 py-2.5 text-[12px] text-amber-200" data-testid="wd-wager-lock">
          <Lock className="h-4 w-4 shrink-0" />
          <span><b>Wager {inr(wagerLeft)} more</b> on cricket or casino to unlock withdrawals (bonus terms).</span>
        </div>
      )}

      <StepRail steps={steps} current={step} onStep={setStep} />

      {step === 1 && (
        <Panel testid="wd-amount-panel">
          <AmountChips chips={CHIPS.filter((c) => c <= balance)} value={n} onPick={(c) => setWd({ ...wd, amount: String(c) })} prefix="wd-quick" />
          <div>
            <AmountField value={wd.amount} onChange={(e) => setWd({ ...wd, amount: e.target.value })} min={MIN_WD} max={balance} valid={amountOk} invalid={n > balance} testid="wd-amount-input" />
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500 px-1">
              <span>Min <span className="text-slate-300 font-semibold">{inr(MIN_WD)}</span></span>
              <span>Max <span className="text-slate-300 font-semibold">{inr(Math.max(0, Math.floor(balance)))}</span></span>
            </div>
            {n > balance && <p className="mt-2 text-xs text-rose-400 px-1" data-testid="wd-amount-error">Amount exceeds your withdrawable balance.</p>}
          </div>
          <Cta onClick={() => setStep(2)} disabled={!amountOk || locked} testid="wd-continue-1">Continue <ArrowRight className="h-5 w-5" /></Cta>
        </Panel>
      )}

      {step === 2 && (
        <Panel testid="wd-bank-panel">
          <div className="grid grid-cols-2 gap-2" data-testid="wd-method-tabs">
            {[["bank", "Bank account", Landmark], ["upi", "UPI ID", Smartphone]].map(([m, label, Icon]) => (
              <button key={m} type="button" onClick={() => setMethod(m)} data-testid={`wd-method-${m}`}
                className={`inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-bold transition-colors ${
                  (wd.method || "bank") === m ? "bg-[#f5b400] text-black ring-1 ring-[#f5b400]" : "bg-white/[0.04] text-white ring-1 ring-white/10 hover:ring-[#f5b400]/50"}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          {isUpi && (
            <div className="space-y-2.5" data-testid="wd-upi-form">
              <Input value={wd.upiId || ""} onChange={(e) => setWd({ ...wd, upiId: e.target.value.replace(/\s/g, "").slice(0, 80) })} placeholder="yourname@upi" inputMode="email" autoCapitalize="none"
                className={`${GLASS_INPUT} lowercase tracking-wide`} data-testid="wd-upi-input" />
              <Input value={wd.accountName} onChange={(e) => setWd({ ...wd, accountName: e.target.value })} placeholder="Name on the UPI account (optional)"
                className={GLASS_INPUT} data-testid="wd-upi-name-input" />
              <p className="text-[11px] text-slate-500 inline-flex items-center gap-1 px-1"><Info className="h-3 w-3" /> Paid to this UPI ID after admin approval · ₹0 fees.</p>
              {wd.upiId && !upiOk && <p className="text-xs text-rose-400 px-1" data-testid="wd-upi-error">Enter a valid UPI ID, e.g. name@okaxis</p>}
            </div>
          )}
          {!isUpi && savedAccounts.length > 0 && (
            <div className="space-y-2">
              {savedAccounts.map((a) => (
                <SavedAccount key={`${a.accountNumber}-${a.ifsc}`} a={a} onPick={() => pickSaved(a)}
                  active={!addNew && selectedSaved && selectedSaved.accountNumber === a.accountNumber} />
              ))}
            </div>
          )}
          {!isUpi && <button type="button" onClick={() => { setAddNew((v) => !v); if (!addNew) setWd({ ...wd, accountName: "", accountNumber: "", ifsc: "" }); }} data-testid="wd-add-new-toggle"
            className="w-full flex items-center justify-center gap-1.5 rounded-2xl border border-dashed border-[#f5b400]/50 bg-[#f5b400]/5 h-12 text-[12px] font-bold uppercase tracking-[0.1em] text-[#f5b400] hover:bg-[#f5b400]/10 transition-colors">
            <Plus className="h-4 w-4" /> {addNew && savedAccounts.length > 0 ? "Use a saved account" : "Add new bank account"}
          </button>}
          {!isUpi && (addNew || savedAccounts.length === 0) && (
            <div className="space-y-2.5" data-testid="wd-new-account-form">
              <Input value={wd.accountNumber} onChange={(e) => setWd({ ...wd, accountNumber: e.target.value.replace(/\D/g, "").slice(0, 18) })} placeholder="Bank account number" inputMode="numeric"
                className={`${GLASS_INPUT} tabular-nums tracking-wider`} data-testid="wd-account-input" />
              <Input value={wd.ifsc} onChange={(e) => setWd({ ...wd, ifsc: e.target.value.toUpperCase().slice(0, 11) })} placeholder="IFSC code"
                className={`${GLASS_INPUT} uppercase tracking-wider`} data-testid="wd-ifsc-input" />
              <Input value={wd.accountName} onChange={(e) => setWd({ ...wd, accountName: e.target.value })} placeholder="Full name as per bank passbook"
                className={GLASS_INPUT} data-testid="wd-name-input" />
              <p className="text-[11px] text-slate-500 inline-flex items-center gap-1 px-1"><Info className="h-3 w-3" /> Up to {savedMax} accounts are saved automatically after each withdrawal.</p>
            </div>
          )}
          <Cta onClick={() => setStep(3)} disabled={!bankOk} testid="wd-continue-2">Continue <ArrowRight className="h-5 w-5" /></Cta>
        </Panel>
      )}

      {step === 3 && (
        <Panel testid="wd-overview-panel">
          <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 divide-y divide-white/5 overflow-hidden text-[13px]">
            {[["Withdrawal amount", inr(n)], ...(isUpi
              ? [["UPI ID", (wd.upiId || "").trim().toLowerCase()], ...(wd.accountName.trim() ? [["Account holder", wd.accountName]] : [])]
              : [["Account holder", wd.accountName], ["Account number", mask(wd.accountNumber)], ["IFSC", wd.ifsc]]),
              ["Processing time", "Within 2 hours after approval"], ["Fees", "₹0"]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{k}</span>
                <span className="text-white font-semibold text-right tabular-nums" data-testid={`wd-ov-${k.split(" ")[0].toLowerCase()}`}>{v}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-2xl bg-[#f5b400]/10 ring-1 ring-[#f5b400]/30 px-3.5 py-2.5">
            <span className="text-sm text-slate-200">Balance after withdrawal</span>
            <span className="font-display font-extrabold text-lg text-[#f5b400] tabular-nums" data-testid="wd-balance-after">{inr(Math.max(0, balance - n))}</span>
          </div>
          <Cta onClick={onSubmit} disabled={submitting || locked} testid="wd-submit-btn">
            {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Placing request…</> : <>Confirm · {inr(n)} <ArrowRight className="h-5 w-5" /></>}
          </Cta>
          <p className="text-[11px] text-slate-500 text-center">Processed after admin approval · you will be notified in Activity Log.</p>
        </Panel>
      )}
    </GlassCard>
  );
}
